import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { payrollService, employeeService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
    Banknote,
    Calculator,
    CheckCircle,
    Download,
    FileText,
    History,
    Loader2,
    Settings2,
} from 'lucide-react';
import { format } from 'date-fns';

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

const YEARS = [2024, 2025, 2026];

export default function PayrollPage() {
    const { user, isAdmin } = useAuth();
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState([]);
    const [myPayslips, setMyPayslips] = useState([]);

    // Admin/HR states
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [payrollLoading, setPayrollLoading] = useState(false);

    // Payroll runs history
    const [payrollRuns, setPayrollRuns] = useState([]);
    const [finalizeLoading, setFinalizeLoading] = useState(null);

    // EPF report state
    const [epfMonth, setEpfMonth] = useState(new Date().getMonth() + 1);
    const [epfYear, setEpfYear] = useState(new Date().getFullYear());

    // Salary Structure states
    const [showSalaryDialog, setShowSalaryDialog] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [salaryForm, setSalaryForm] = useState({
        // Earnings
        basic_salary: 0,
        hra: 0,
        conveyance_allowance: 1600,
        medical_allowance: 1250,
        special_allowance: 0,
        lta: 0,
        other_allowances: 0,
        // Deductions
        epf_applicable: true,
        esi_applicable: false,
        professional_tax: 200,
        mediclaim: 0,
        tds: 0,
        other_deductions: 0,
        other_deductions_reason: '',
        effective_from: format(new Date(), 'yyyy-MM-dd'),
    });

    const canManagePayroll = isAdmin();

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                if (canManagePayroll) {
                    const [emps, runs] = await Promise.all([
                        employeeService.list(),
                        payrollService.getRuns(),
                    ]);
                    setEmployees(emps);
                    setPayrollRuns(runs);
                } else {
                    const slips = await payrollService.getMyPayslips();
                    setMyPayslips(slips);
                }
            } catch (error) {
                console.error('Failed to load payroll data:', error);
                toast.error('Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [canManagePayroll]);

    const handleOpenSalary = async (emp) => {
        setEditingEmployee(emp);
        try {
            const struct = await payrollService.getSalaryStructure(emp.id);
            if (struct) {
                setSalaryForm({
                    basic_salary: struct.basic_salary || 0,
                    hra: struct.hra || 0,
                    conveyance_allowance: struct.conveyance_allowance ?? struct.conveyance ?? 1600,
                    medical_allowance: struct.medical_allowance ?? 1250,
                    special_allowance: struct.special_allowance || 0,
                    lta: struct.lta || 0,
                    other_allowances: struct.other_allowances || 0,
                    epf_applicable: struct.epf_applicable ?? true,
                    esi_applicable: struct.esi_applicable ?? false,
                    professional_tax: struct.professional_tax ?? 200,
                    mediclaim: struct.mediclaim || 0,
                    tds: struct.tds || 0,
                    other_deductions: struct.other_deductions || 0,
                    other_deductions_reason: struct.other_deductions_reason || '',
                    effective_from: struct.effective_from || format(new Date(), 'yyyy-MM-dd'),
                });
            } else {
                setSalaryForm({
                    basic_salary: 0,
                    hra: 0,
                    conveyance_allowance: 1600,
                    medical_allowance: 1250,
                    special_allowance: 0,
                    lta: 0,
                    other_allowances: 0,
                    epf_applicable: true,
                    esi_applicable: false,
                    professional_tax: 200,
                    mediclaim: 0,
                    tds: 0,
                    other_deductions: 0,
                    other_deductions_reason: '',
                    effective_from: format(new Date(), 'yyyy-MM-dd'),
                });
            }
            setShowSalaryDialog(true);
        } catch (error) {
            toast.error('Failed to load salary structure');
        }
    };

    const handleSaveSalary = async () => {
        if (!salaryForm.basic_salary || salaryForm.basic_salary <= 0) {
            toast.error('Basic Salary is required');
            return;
        }
        try {
            await payrollService.createSalaryStructure({
                employee_id: editingEmployee.id,
                ...salaryForm,
            });
            toast.success('Salary structure updated');
            setShowSalaryDialog(false);
        } catch (error) {
            toast.error('Failed to update salary');
        }
    };

    const handleRunPayroll = async () => {
        setPayrollLoading(true);
        try {
            const result = await payrollService.runPayroll({ month: selectedMonth, year: selectedYear });
            toast.success(`Payroll generated: ${result.payslips_generated} payslips created`);
            const runs = await payrollService.getRuns();
            setPayrollRuns(runs);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to run payroll');
        } finally {
            setPayrollLoading(false);
        }
    };

    const handleFinalizePayroll = async (runId) => {
        setFinalizeLoading(runId);
        try {
            const result = await payrollService.finalizePayroll(runId);
            toast.success(result.message || 'Payroll finalized');
            const runs = await payrollService.getRuns();
            setPayrollRuns(runs);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to finalize payroll');
        } finally {
            setFinalizeLoading(null);
        }
    };

    const handleDownloadEpf = async () => {
        try {
            const data = await payrollService.getEpfReport(epfMonth, epfYear);
            const rows = [['Employee Code', 'Employee Name', 'UAN', 'Gross', 'EPF Employee', 'EPF Employer']];
            data.forEach(r => rows.push([r.employee_code, r.employee_name, r.uan || '', r.gross_salary, r.epf_employee, r.epf_employer]));
            const csv = rows.map(r => r.join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `EPF_Report_${epfMonth}_${epfYear}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            toast.success('EPF report downloaded');
        } catch (error) {
            toast.error('Failed to download EPF report');
        }
    };

    const handleDownloadPayslip = async (payslipId, month, year) => {
        try {
            const blob = await payrollService.downloadPayslip(payslipId);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Payslip_${month}_${year}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            toast.error('Failed to download payslip');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="space-y-6 fade-in">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Payroll</h1>
                    <p className="text-muted-foreground mt-1">
                        {canManagePayroll
                            ? 'Manage employee salary structures and process monthly payroll'
                            : 'View and download your monthly payslips'}
                    </p>
                </div>
            </div>

            <Tabs defaultValue={canManagePayroll ? "management" : "my-payslips"} className="w-full">
                <TabsList>
                    {canManagePayroll && (
                        <TabsTrigger value="management">
                            <Banknote className="w-4 h-4 mr-2" />
                            Payroll Management
                        </TabsTrigger>
                    )}
                    <TabsTrigger value="my-payslips">
                        <FileText className="w-4 h-4 mr-2" />
                        My Payslips
                    </TabsTrigger>
                    {canManagePayroll && (
                        <TabsTrigger value="settings">
                            <Settings2 className="w-4 h-4 mr-2" />
                            Salary Config
                        </TabsTrigger>
                    )}
                </TabsList>

                {/* ADMIN: Payroll Processing */}
                {canManagePayroll && (
                    <TabsContent value="management" className="space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle>Process Monthly Payroll</CardTitle>
                                <CardDescription>Generate payslips for all active employees</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-col sm:flex-row gap-4 items-end bg-secondary/20 p-4 rounded-lg">
                                    <div className="space-y-2">
                                        <Label>Month</Label>
                                        <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                                            <SelectTrigger className="w-40">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {MONTHS.map(m => <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Year</Label>
                                        <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                                            <SelectTrigger className="w-32">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {YEARS.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Button
                                        className="glow-primary"
                                        onClick={handleRunPayroll}
                                        disabled={payrollLoading}
                                    >
                                        {payrollLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
                                        Run Payroll
                                    </Button>
                                </div>

                                <div className="mt-6 border rounded-lg overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Period</TableHead>
                                                <TableHead>Payslips</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead className="text-right">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {payrollRuns.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                                                        <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                                        No payroll runs yet
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                payrollRuns.map((run) => (
                                                    <TableRow key={run.id}>
                                                        <TableCell className="font-medium">
                                                            {MONTHS.find(m => m.value === run.month)?.label} {run.year}
                                                        </TableCell>
                                                        <TableCell>{run.payslips_count ?? '—'}</TableCell>
                                                        <TableCell>
                                                            <Badge variant={run.status === 'finalized' ? 'default' : 'secondary'}>
                                                                {run.status}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            {run.status !== 'finalized' && (
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => handleFinalizePayroll(run.id)}
                                                                    disabled={finalizeLoading === run.id}
                                                                >
                                                                    {finalizeLoading === run.id
                                                                        ? <Loader2 className="w-4 h-4 animate-spin" />
                                                                        : <CheckCircle className="w-4 h-4 mr-1" />}
                                                                    Finalize
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>

                                {/* EPF Report Download */}
                                <div className="mt-6 p-4 border rounded-lg bg-secondary/10">
                                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                                        <Download className="w-4 h-4" /> EPF Report
                                    </h3>
                                    <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 items-start sm:items-end">
                                        <div className="space-y-1">
                                            <Label>Month</Label>
                                            <Select value={epfMonth.toString()} onValueChange={(v) => setEpfMonth(parseInt(v))}>
                                                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                                                <SelectContent>{MONTHS.map(m => <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1">
                                            <Label>Year</Label>
                                            <Select value={epfYear.toString()} onValueChange={(v) => setEpfYear(parseInt(v))}>
                                                <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                                                <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <Button variant="outline" onClick={handleDownloadEpf}>
                                            <Download className="w-4 h-4 mr-2" /> Download CSV
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                )}

                {/* EMPLOYEE: My Payslips */}
                <TabsContent value="my-payslips">
                    <Card>
                        <CardHeader>
                            <CardTitle>My Payslips</CardTitle>
                            <CardDescription>Your historical earnings and deductions</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Period</TableHead>
                                        <TableHead className="hidden sm:table-cell">Gross Salary</TableHead>
                                        <TableHead className="hidden md:table-cell">Deductions</TableHead>
                                        <TableHead>Net Pay</TableHead>
                                        <TableHead className="text-right">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {myPayslips.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                                No payslips found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        myPayslips.map((slip) => (
                                            <TableRow key={slip.id}>
                                                <TableCell className="font-medium">
                                                    {MONTHS.find(m => m.value === slip.month)?.label} {slip.year}
                                                </TableCell>
                                                <TableCell className="hidden sm:table-cell">₹{slip.gross_salary.toLocaleString()}</TableCell>
                                                <TableCell className="hidden md:table-cell text-red-500">₹{slip.total_deductions.toLocaleString()}</TableCell>
                                                <TableCell className="font-bold text-green-600">₹{slip.net_salary.toLocaleString()}</TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="sm" onClick={() => handleDownloadPayslip(slip.id, slip.month, slip.year)}>
                                                        <Download className="w-4 h-4 mr-2" />
                                                        PDF
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ADMIN: Salary Config */}
                {canManagePayroll && (
                    <TabsContent value="settings">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle>Salary Structures</CardTitle>
                                        <CardDescription>Indian Payroll Standard — Configure Basic, HRA, Conveyance, Medical, LTA, PF, ESI, PT, Mediclaim &amp; TDS per employee</CardDescription>
                                    </div>
                                    <Badge variant="outline" className="text-xs font-semibold border-green-500 text-green-700">
                                        Indian Payroll Standard
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Employee</TableHead>
                                            <TableHead>Department</TableHead>
                                            <TableHead>Designation</TableHead>
                                            <TableHead className="text-right">Action</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {employees.filter(e => e.is_active).map((emp) => (
                                            <TableRow key={emp.id}>
                                                <TableCell className="font-medium">{emp.first_name} {emp.last_name}</TableCell>
                                                <TableCell><Badge variant="outline">{emp.department}</Badge></TableCell>
                                                <TableCell>{emp.designation}</TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="outline" size="sm" onClick={() => handleOpenSalary(emp)}>
                                                        <Settings2 className="w-4 h-4 mr-2" />
                                                        Configure
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                )}
            </Tabs>

            {/* Indian Payroll Salary Config Dialog */}
            <Dialog open={showSalaryDialog} onOpenChange={setShowSalaryDialog}>
                <DialogContent className="max-w-3xl max-h-[90dvh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Settings2 className="w-5 h-5" />
                            Indian Payroll Configuration
                        </DialogTitle>
                        <CardDescription>
                            {editingEmployee?.first_name} {editingEmployee?.last_name} &mdash; Monthly Salary Structure
                        </CardDescription>
                    </DialogHeader>

                    {/* Live Summary Bar */}
                    {(() => {
                        const gross = (salaryForm.basic_salary || 0) + (salaryForm.hra || 0) + (salaryForm.conveyance_allowance || 0) + (salaryForm.medical_allowance || 0) + (salaryForm.special_allowance || 0) + (salaryForm.lta || 0) + (salaryForm.other_allowances || 0);
                        const epfDed = salaryForm.epf_applicable ? Math.round(Math.min(salaryForm.basic_salary || 0, 15000) * 0.12) : 0;
                        const esiDed = (salaryForm.esi_applicable && gross <= 21000) ? Math.round(gross * 0.0075) : 0;
                        const totalDed = epfDed + esiDed + (salaryForm.professional_tax || 0) + (salaryForm.mediclaim || 0) + (salaryForm.tds || 0) + (salaryForm.other_deductions || 0);
                        const net = gross - totalDed;
                        return (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-3 bg-secondary/10 p-3 rounded-lg text-center">
                                <div>
                                    <p className="text-xs text-muted-foreground">Gross Salary</p>
                                    <p className="font-bold text-base text-green-600">₹{gross.toLocaleString('en-IN')}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Total Deductions</p>
                                    <p className="font-bold text-base text-red-500">₹{totalDed.toLocaleString('en-IN')}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Net Take-Home</p>
                                    <p className="font-bold text-base text-blue-600">₹{net.toLocaleString('en-IN')}</p>
                                </div>
                            </div>
                        );
                    })()}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-2">
                        {/* EARNINGS */}
                        <div className="space-y-3">
                            <h3 className="text-sm font-semibold border-b pb-1 text-green-700">Earnings (Monthly)</h3>

                            <div className="space-y-1">
                                <Label>Basic Salary *</Label>
                                <Input type="number" min="0" value={salaryForm.basic_salary}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, basic_salary: parseFloat(e.target.value) || 0 })} />
                                <p className="text-xs text-muted-foreground">Typically 40–50% of CTC. EPF & PT are calculated on this.</p>
                            </div>

                            <div className="space-y-1">
                                <Label>House Rent Allowance (HRA)</Label>
                                <Input type="number" min="0" value={salaryForm.hra}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, hra: parseFloat(e.target.value) || 0 })} />
                                <p className="text-xs text-muted-foreground">40% of Basic (non-metro) / 50% (metro).</p>
                            </div>

                            <div className="space-y-1">
                                <Label>Conveyance Allowance</Label>
                                <Input type="number" min="0" value={salaryForm.conveyance_allowance}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, conveyance_allowance: parseFloat(e.target.value) || 0 })} />
                                <p className="text-xs text-muted-foreground">Standard ₹1,600/month (tax exempt).</p>
                            </div>

                            <div className="space-y-1">
                                <Label>Medical Allowance</Label>
                                <Input type="number" min="0" value={salaryForm.medical_allowance}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, medical_allowance: parseFloat(e.target.value) || 0 })} />
                                <p className="text-xs text-muted-foreground">Standard ₹1,250/month (tax exempt up to ₹15,000/yr).</p>
                            </div>

                            <div className="space-y-1">
                                <Label>Special Allowance</Label>
                                <Input type="number" min="0" value={salaryForm.special_allowance}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, special_allowance: parseFloat(e.target.value) || 0 })} />
                                <p className="text-xs text-muted-foreground">Balancing / performance component. Fully taxable.</p>
                            </div>

                            <div className="space-y-1">
                                <Label>LTA (Leave Travel Allowance)</Label>
                                <Input type="number" min="0" value={salaryForm.lta}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, lta: parseFloat(e.target.value) || 0 })} />
                                <p className="text-xs text-muted-foreground">Tax exempt on actual travel bills (once in 2 yrs).</p>
                            </div>

                            <div className="space-y-1">
                                <Label>Other Allowances</Label>
                                <Input type="number" min="0" value={salaryForm.other_allowances}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, other_allowances: parseFloat(e.target.value) || 0 })} />
                            </div>
                        </div>

                        {/* DEDUCTIONS */}
                        <div className="space-y-3">
                            <h3 className="text-sm font-semibold border-b pb-1 text-red-700">Deductions & Statutory (Monthly)</h3>

                            {/* EPF */}
                            <div className="p-3 border rounded-lg space-y-2 bg-secondary/5">
                                <div className="flex items-center justify-between">
                                    <Label className="font-semibold">Provident Fund (PF / EPF)</Label>
                                    <div className="flex items-center gap-2">
                                        <input type="checkbox" id="epf_app" checked={salaryForm.epf_applicable}
                                            onChange={(e) => setSalaryForm({ ...salaryForm, epf_applicable: e.target.checked })}
                                            className="w-4 h-4" />
                                        <label htmlFor="epf_app" className="text-sm">Applicable</label>
                                    </div>
                                </div>
                                {salaryForm.epf_applicable && (
                                    <div className="text-xs text-muted-foreground space-y-0.5">
                                        <p>Employee: 12% of Basic (max ₹15,000 ceiling) = <span className="font-semibold text-foreground">₹{Math.round(Math.min(salaryForm.basic_salary || 0, 15000) * 0.12).toLocaleString('en-IN')}</span></p>
                                        <p>Employer: 12% of Basic (max ₹15,000 ceiling) = <span className="font-semibold text-foreground">₹{Math.round(Math.min(salaryForm.basic_salary || 0, 15000) * 0.12).toLocaleString('en-IN')}</span></p>
                                    </div>
                                )}
                            </div>

                            {/* ESI */}
                            <div className="p-3 border rounded-lg space-y-2 bg-secondary/5">
                                <div className="flex items-center justify-between">
                                    <Label className="font-semibold">ESI (Employee State Insurance)</Label>
                                    <div className="flex items-center gap-2">
                                        <input type="checkbox" id="esi_app" checked={salaryForm.esi_applicable}
                                            onChange={(e) => setSalaryForm({ ...salaryForm, esi_applicable: e.target.checked })}
                                            className="w-4 h-4" />
                                        <label htmlFor="esi_app" className="text-sm">Applicable</label>
                                    </div>
                                </div>
                                {salaryForm.esi_applicable && (
                                    <p className="text-xs text-muted-foreground">
                                        Applicable only if Gross ≤ ₹21,000. Employee: 0.75%, Employer: 3.25%.
                                    </p>
                                )}
                            </div>

                            <div className="space-y-1">
                                <Label>Professional Tax (PT)</Label>
                                <Input type="number" min="0" value={salaryForm.professional_tax}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, professional_tax: parseFloat(e.target.value) || 0 })} />
                                <p className="text-xs text-muted-foreground">State slab-based. Typically ₹200/month (max ₹2,400/yr).</p>
                            </div>

                            <div className="space-y-1">
                                <Label>Mediclaim / Group Health Insurance</Label>
                                <Input type="number" min="0" value={salaryForm.mediclaim}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, mediclaim: parseFloat(e.target.value) || 0 })} />
                                <p className="text-xs text-muted-foreground">Monthly premium deducted from salary.</p>
                            </div>

                            <div className="space-y-1">
                                <Label>TDS / Income Tax</Label>
                                <Input type="number" min="0" value={salaryForm.tds}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, tds: parseFloat(e.target.value) || 0 })} />
                                <p className="text-xs text-muted-foreground">Monthly TDS based on annual tax projection.</p>
                            </div>

                            <div className="space-y-1">
                                <Label>Other Deductions</Label>
                                <Input type="number" min="0" value={salaryForm.other_deductions}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, other_deductions: parseFloat(e.target.value) || 0 })} />
                            </div>

                            {salaryForm.other_deductions > 0 && (
                                <div className="space-y-1">
                                    <Label>Reason for Other Deductions</Label>
                                    <Input type="text" value={salaryForm.other_deductions_reason}
                                        onChange={(e) => setSalaryForm({ ...salaryForm, other_deductions_reason: e.target.value })} />
                                </div>
                            )}

                            <div className="space-y-1 pt-1">
                                <Label>Effective From Date</Label>
                                <Input type="date" value={salaryForm.effective_from}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, effective_from: e.target.value })} />
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setShowSalaryDialog(false)}>Cancel</Button>
                        <Button className="glow-primary" onClick={handleSaveSalary}>
                            Save Salary Structure
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
